/* Pakyo theme — header menu, FAQ, and data-driven home sections. */
(() => {
  const P = window.Pakyo || {};
  const PT = window.PT; // text in the visitor's language (snippets/i18n-js.liquid)
  const CONTS = ['Asia', 'Europe', 'Middle East', 'Africa', 'North America', 'Latin America', 'Oceania'];

  const esc = s => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  // Supplier cost (USD) → retail (plan B, same as scripts/catalog.py) → shopper's currency.
  const retail = cost => {
    const pr = P.pricing || { mult: 1.7, fixed: 1.5 };
    const p = cost * pr.mult + pr.fixed;
    return p < 10 ? Math.ceil(p * 2) / 2 - 0.01 : Math.ceil(p) - 0.01;
  };
  // Shopify Markets rounds converted prices UP to a step per currency. Steps read from Shopify's own converted prices
  // (Admin contextualPricing, 52 prices × 107 currencies, 2026-10-03): default 1 unit, EUR to x.95, these to 100 / 1000.
  const ROUND_100 = 'AFN ALL AMD BDT CRC CVE DJF DOP DZD GYD HUF INR ISK JMD JPY KES KGS KMF KZT LKR MKD MWK NGN NPR PHP PKR RSD RWF VUV XAF XOF XPF YER';
  const ZERO_DEC = 'BIF CLP DJF GNF ISK JPY KMF KRW PYG RWF UGX UYI VND VUV XAF XOF XPF';
  const THREE_DEC = 'BHD IQD JOD KWD LYD OMR TND';
  const ROUND_1000 = 'BIF CDF GNF IDR KHR KRW LAK LBP MMK MNT PYG SLL STD TZS UGX UZS VND';
  // A USD retail price → cents in the shopper's currency, exactly as checkout will charge it.
  P.localCents = usd => {
    const cur = (window.Shopify && Shopify.currency) || {};
    const rate = +(cur.rate || 1), code = cur.active || 'USD';
    if (rate === 1 && code === 'USD') return Math.round(usd * 100);
    // Shopify first rounds the conversion to the currency's minor unit (12.99 × 0.7702 = 10.0049 → £10.00 → £10; VND has none)
    // minor units per ISO 4217 (not Intl, which says 0 for ALL/AFN where Shopify uses 2)
    const digits = ZERO_DEC.includes(code) ? 0 : THREE_DEC.includes(code) ? 3 : 2;
    const x = Math.round(usd * rate * 10 ** digits) / 10 ** digits;
    const step = ROUND_1000.includes(code) ? 1000 : ROUND_100.includes(code) ? 100 : 1;
    const v = code === 'EUR' ? Math.ceil(x - 0.95 - 1e-9) + 0.95 : Math.ceil(x / step - 1e-9) * step;
    return Math.round(v * 100);
  };
  const money = cost => P.formatMoney(P.localCents(retail(cost)));
  const flag = iso => iso && /^[A-Z]{2}$/.test(iso) ? `https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.2.3/flags/1x1/${iso.toLowerCase()}.svg` : '';
  // Multi-country plans: up to 3 overlapping member flags (bundles.json `flags`) instead of a numbered disc, which read as a rank.
  const flagStack = (codes, cls) => `<span class="flag-stack ${cls}" aria-hidden="true">${(codes || []).map(c => flag(String(c).toUpperCase())).filter(Boolean).map(u => `<img src="${u}" alt="" loading="lazy">`).join('')}</span>`;
  // Accent-insensitive search text including aliases (design: countries.json `aka`).
  const fold = t => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  // Names as shown (visitor's language); data and links stay on the English name. Search matches both.
  const nm = o => o.iso ? PT.country(o.key || o.iso, o.name) : o.l && o.l[PT.lang] || o.name;
  const contL = c => c ? PT('cont_' + c.toLowerCase().replace(/ /g, '_')) : '';
  const col = PT.collator;
  const hay = o => fold([o.name, o.label || nm(o)].concat(o.aka || []).join(' | '));
  const splitNames = s => (s || '').split(',').map(x => x.trim()).filter(Boolean);

  // "Japan: japan-esim" lines from theme settings → { Japan: 'japan-esim' }
  const handles = {};
  (P.productHandles || '').split('\n').forEach(line => {
    const i = line.indexOf(':');
    if (i > 0) handles[line.slice(0, i).trim().toLowerCase()] = line.slice(i + 1).trim();
  });
  const hrefFor = name => {
    const dest = (P.destinations || {})[name.toLowerCase()];
    if (dest) return dest;
    const h = handles[name.toLowerCase()];
    return h ? P.routes.productPrefix + h : window.PT.url('pages/destinations?q=') + encodeURIComponent(name);
  };

  // Destination page URL if one exists (no fallback) — used where linking back to the list would loop.
  const pageFor = name => (P.destinations || {})[name.toLowerCase()] || '';

  let dataPromise;
  const loadData = () => dataPromise || (dataPromise = Promise.all([
    fetch(P.data.countries).then(r => r.json()),
    fetch(P.data.bundles).then(r => r.json())
  ]).then(([countries, bundles]) => ({ countries, bundles })));

  /* ---------- Money (Shopify money_format, presentment currency cents) ---------- */
  // Non-USD currencies carry their code like Shopify's money_with_currency ("$11.00 CAD"), matching checkout.
  P.formatMoney = cents => {
    const cur = window.Shopify && Shopify.currency && Shopify.currency.active;
    return cur && cur !== 'USD' ? `${P.formatMoneyRaw(cents)} ${cur}` : P.formatMoneyRaw(cents);
  };
  P.formatMoneyRaw = cents => {
    const fmt = P.moneyFormat || '${{amount}}';
    const num = (n, dec, th = ',', dp = '.') => {
      const [i, d] = (n / 100).toFixed(dec).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (d ? dp + d : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: num(cents, 2), amount_no_decimals: num(cents, 0),
      amount_with_comma_separator: num(cents, 2, '.', ','), amount_no_decimals_with_comma_separator: num(cents, 0, '.', ','),
      amount_with_apostrophe_separator: num(cents, 2, "'", '.'), amount_with_space_separator: num(cents, 2, ' ', ',')
    })[k] ?? num(cents, 2));
  };

  /* ---------- Cart drawer (design §3) ---------- */
  // One cart, two places: the drawer and the /cart page (sections/main-default.liquid), both rendered from /cart.js.
  const drawer = document.querySelector('[data-cart-drawer]');
  const cartPage = document.querySelector('[data-cart-page]');
  if (drawer || cartPage) {
    const m = c => P.formatMoney(c);
    const flagUrl = iso => iso ? `https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.2.3/flags/1x1/${String(iso).toLowerCase()}.svg` : '';
    const setCount = n => document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = n; el.hidden = !n; });
    const views = [
      drawer && { root: drawer, itemsEl: drawer.querySelector('[data-cart-items]'), footEl: drawer.querySelector('[data-cart-foot]'), countEl: drawer.querySelector('[data-cart-drawer-count]'), page: false },
      cartPage && { root: cartPage, itemsEl: cartPage.querySelector('[data-cart-items]'), footEl: cartPage.querySelector('[data-cart-foot]'), countEl: cartPage.querySelector('[data-cart-page-count]'), page: true }
    ].filter(Boolean);

    // Group discounts are automatic product discounts, so their titles live on the line allocations.
    const discTitle = cart => {
      // Shopify can't translate discount titles; ours are shown in the visitor's language by title
      const own = t => ({ 'Group discount · 5% off 2 eSIMs': PT('group_discount_5'), 'Group discount · 10% off 3+ eSIMs': PT('group_discount_10') })[t] || t;
      for (const i of cart.items) for (const a of i.line_level_discount_allocations || []) if (a.discount_application && a.discount_application.title) return own(a.discount_application.title);
      return own((cart.cart_level_discount_applications[0] || {}).title) || PT('group_discount');
    };

    const renderView = (v, cart) => {
      const n = cart.item_count;
      if (v.countEl) v.countEl.textContent = n ? `(${n})` : '';
      if (!n) {
        v.itemsEl.innerHTML = `<div class="cart-empty"><div class="cart-empty__title">${esc(PT('cart_empty'))}</div><div class="cart-empty__sub">${esc(PT('cart_empty_sub'))}</div><a class="btn-dark" href="${PT.url('pages/destinations')}">${esc(PT('browse_dests'))}</a></div>`;
        v.footEl.hidden = true;
        return;
      }
      const eN = cart.items.filter(i => !(i.properties && i.properties._local)).reduce((a, i) => a + i.quantity, 0); // eSIMs that count for the group discount
      v.itemsEl.innerHTML = cart.items.map(i => {
        const iso = i.properties && i.properties._iso;
        return `<div class="cart-item" data-key="${esc(i.key)}">
          <div class="cart-item__top">${iso === 'multi' ? '<span class="cart-item__multi" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3z"/></svg></span>' : iso ? `<img class="flag" src="${flagUrl(iso)}" alt="" width="40" height="40">` : ''}
            <div class="cart-item__text"><div class="cart-item__title">${esc(i.product_title)}</div><div class="cart-item__summary">${esc(i.variant_title || '')}</div></div>
            <button type="button" class="cart-item__remove" data-cart-qty="0" aria-label="${esc(PT('remove'))}"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13"/></svg></button></div>
          <div class="cart-item__row"><div class="qty-pill"><button type="button" data-cart-qty="${i.quantity - 1}" aria-label="${esc(PT('decrease'))}"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M5 12h14"/></svg></button><span>${i.quantity}</span><button type="button" data-cart-qty="${i.quantity + 1}" aria-label="${esc(PT('increase'))}"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg></button></div>
            <div class="cart-item__total">${m(i.final_line_price)}</div></div>
          ${eN === 1 && !(i.properties && i.properties._local) ? `<button type="button" class="cart-item__nudge" data-cart-qty="2">${esc(PT('cart_nudge_a'))}<b>${esc(PT('cart_nudge_b'))}</b>${esc(PT('cart_nudge_c'))}</button>` : ''}
        </div>`;
      }).join('');
      const nudge = eN === 1 ? PT('cart_more_5') : eN === 2 ? PT('cart_more_10') : eN >= 3 ? PT('cart_best') : '';
      const disc = cart.original_total_price - cart.total_price;
      v.footEl.innerHTML = `${nudge ? `<div class="nudge"><div class="nudge__text">${esc(nudge)}</div><div class="nudge__track"><div class="nudge__bar${eN >= 3 ? ' is-done' : ''}" style="width:${Math.min(eN, 3) / 3 * 100}%"></div></div></div>` : ''}
        ${disc > 0 ? `<div class="cart-line"><span>${esc(PT('cart_items'))}</span><span>${m(cart.original_total_price)}</span></div><div class="cart-line cart-line--green"><span>${esc(discTitle(cart))}</span><span>−${m(disc)}</span></div>` : ''}
        <div class="cart-subtotal"><span>${esc(PT('cart_subtotal'))}</span><span>${m(cart.total_price)}</span></div>
        <div class="cart-note">${esc(PT('cart_note'))}</div>
        <a class="btn-primary" href="${PT.url('checkout')}">${esc(PT('checkout'))}</a>
        ${v.page ? `<a class="btn-outline btn-outline--sm" href="${PT.url('pages/destinations')}">${esc(PT('continue_shopping'))}</a>` : `<button type="button" class="btn-outline btn-outline--sm" data-cart-close>${esc(PT('continue_shopping'))}</button>`}`;
      v.footEl.hidden = false;
    };
    const render = cart => { setCount(cart.item_count); views.forEach(v => renderView(v, cart)); };

    const load = () => fetch(window.PT.url('cart.js'), { headers: { Accept: 'application/json' } }).then(r => r.json()).then(render);
    const open = () => { if (!drawer) return; drawer.hidden = false; document.documentElement.classList.add('no-scroll'); load(); };
    const close = () => { if (!drawer) return; drawer.hidden = true; document.documentElement.classList.remove('no-scroll'); };
    if (cartPage) load();

    document.addEventListener('click', e => {
      if (e.target.closest('[data-cart-open]')) { e.preventDefault(); open(); return; }
      if (e.target.closest('[data-cart-close]')) { close(); return; }
      const q = e.target.closest('[data-cart-qty]');
      if (q && views.some(v => v.root.contains(q))) {
        const key = q.closest('[data-key]').dataset.key;
        fetch(window.PT.url('cart/change.js'), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity: Math.max(0, +q.dataset.cartQty) }) })
          .then(r => r.json()).then(render);
      }
    });
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && drawer && !drawer.hidden) close(); });
    document.addEventListener('pakyo:cart-open', open);
  }

  const findCountry = (D, n) => D.find(o => o.name.toLowerCase() === n.toLowerCase()) || D.find(o => o.name.toLowerCase().startsWith(n.toLowerCase()));

  /* ---------- Prices rendered server-side as supplier cost ---------- */
  document.querySelectorAll('[data-cost]').forEach(el => { el.textContent = money(parseFloat(el.dataset.cost)); });

  /* ---------- Header menu ---------- */
  document.querySelectorAll('[data-header]').forEach(h => {
    const btn = h.querySelector('[data-menu-toggle]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      const open = h.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open);
    });
  });

  /* ---------- Language & currency panel ---------- */
  document.querySelectorAll('[data-header]').forEach(h => {
    const panel = h.querySelector('[data-locale-panel]');
    if (!panel) return;
    const openers = h.querySelectorAll('[data-locale-open]');
    const set = open => { panel.hidden = !open; openers.forEach(b => b.setAttribute('aria-expanded', open)); };
    openers.forEach(b => b.addEventListener('click', e => { e.stopPropagation(); set(panel.hidden); }));
    h.querySelector('[data-locale-close]')?.addEventListener('click', () => set(false));
    document.addEventListener('click', e => { if (!panel.hidden && !panel.contains(e.target)) set(false); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape') set(false); });
    // Country names follow the language picked here (before saving), sorted the way that language sorts.
    const langSel = panel.querySelector('[data-locale-lang]'), ctySel = panel.querySelector('[data-locale-country]');
    let data = null;
    try { data = JSON.parse(panel.querySelector('[data-country-names]').textContent); } catch (e) { /* keep the server labels */ }
    const sortBy = lang => {
      const col = new Intl.Collator((window.PAKYO_COLLATORS || {})[lang] || lang);
      [...ctySel.options].sort((a, b) => col.compare(a.textContent, b.textContent)).forEach(o => ctySel.appendChild(o));
    };
    if (langSel && ctySel) {
      langSel.addEventListener('change', () => {
        const i = data ? data.langs.indexOf(langSel.value) : -1;
        if (i >= 0) [...ctySel.options].forEach(o => { const n = (data.names[o.value] || [])[i]; if (n) o.textContent = n + ' · ' + o.dataset.cur; });
        sortBy(langSel.value);
      });
      sortBy(langSel.value); // server labels already match the page language; just order them the way it sorts
    }
  });

  /* ---------- FAQ: one open at a time ---------- */
  document.querySelectorAll('[data-faq]').forEach(root => {
    root.addEventListener('click', e => {
      const q = e.target.closest('.faq__q');
      if (!q) return;
      const item = q.parentElement;
      const wasOpen = item.classList.contains('is-open');
      root.querySelectorAll('.faq__item').forEach(it => {
        it.classList.remove('is-open');
        it.querySelector('.faq__q').setAttribute('aria-expanded', 'false');
      });
      if (!wasOpen) { item.classList.add('is-open'); q.setAttribute('aria-expanded', 'true'); }
    });
  });

  /* ---------- Hero search ---------- */
  document.querySelectorAll('[data-hero-search]').forEach(root => {
    const box = root.querySelector('[data-search-box]');
    const input = root.querySelector('[data-search-input]');
    const clear = root.querySelector('[data-search-clear]');
    const drop = root.querySelector('[data-search-drop]');
    const form = root.querySelector('form');
    const popularNames = splitNames(root.dataset.popular);
    let D = [], res = [], hi = 0, focused = false;

    const scrollToId = id => {
      const el = document.getElementById(id);
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
    };

    const render = () => {
      const t = fold(input.value.trim());
      clear.hidden = !input.value;
      const matched = t ? D.filter(o => hay(o).includes(t) || o.iso.toLowerCase() === t)
        .sort((a, b) => (fold(a.name).startsWith(t) || fold(nm(a)).startsWith(t) ? 0 : 1) - (fold(b.name).startsWith(t) || fold(nm(b)).startsWith(t) ? 0 : 1)).slice(0, 6) : [];
      res = t ? matched : popularNames.map(n => findCountry(D, n)).filter(Boolean).slice(0, 5);
      // Stays open on an exact name too ("canada") — picking a row navigates away anyway.
      const open = focused && (res.length > 0 || !!t) && D.length > 0;
      drop.hidden = !open;
      input.setAttribute('aria-expanded', open);
      if (!open) return;
      hi = Math.min(hi, Math.max(res.length - 1, 0));
      let html = `<div class="search-drop__title">${esc(PT(t ? 'dests_title' : 'popular'))}</div>`;
      html += res.map((o, i) => {
        const also = o.tag === 'number' ? PT('also_number') : o.tag === 'local' ? PT('also_local') : '';
        const sub = contL(o.cont) + (o.bundles ? PT.n('also_in', o.bundles) : '');
        return `<a class="search-row${i === hi ? ' is-hi' : ''}" role="option" href="${esc(hrefFor(o.name))}" data-i="${i}">
          ${flag(o.iso) ? `<img class="flag" src="${flag(o.iso)}" alt="" width="34" height="34">` : ''}
          <span class="search-row__main"><span class="search-row__name">${esc(nm(o))}</span><span class="search-row__sub">${esc(sub)}</span>${also ? `<span class="search-row__also" data-also>${esc(also)} →</span>` : ''}</span>
          <span class="search-row__price">${PT('from', { price: `<b>${money(o.from)}</b>` })}</span></a>`;
      }).join('');
      if (t && matched.length === 0) html += `<div class="search-none">${esc(PT('search_none', { q: input.value.trim() }))} <a href="#browse" data-regional>${esc(PT('try_regional'))}</a></div>`;
      drop.innerHTML = html;
    };

    loadData().then(d => { D = d.countries; render(); }).catch(() => {});
    input.addEventListener('input', () => { focused = true; hi = 0; render(); });
    input.addEventListener('focus', () => { focused = true; box.classList.add('is-focus'); render(); });
    input.addEventListener('blur', () => { focused = false; box.classList.remove('is-focus'); render(); });
    clear.addEventListener('click', () => { input.value = ''; hi = 0; input.focus(); render(); });
    // mousedown so the click lands before input blur closes the list
    drop.addEventListener('mousedown', e => {
      if (e.target.closest('[data-also]')) { e.preventDefault(); input.blur(); scrollToId('local'); return; }
      if (e.target.closest('[data-regional]')) { e.preventDefault(); input.blur(); scrollToId('browse'); return; }
      e.preventDefault();
      const row = e.target.closest('.search-row');
      if (row) window.location.href = row.href;
    });
    input.addEventListener('keydown', e => {
      if (drop.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); hi = Math.min(hi + 1, res.length - 1); render(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); hi = Math.max(hi - 1, 0); render(); }
      else if (e.key === 'Escape') input.blur();
    });
    form.addEventListener('submit', e => {
      const t = input.value.trim().toLowerCase();
      const pick = !drop.hidden && res[hi] ? res[hi] : D.find(o => fold(o.name) === fold(t) || fold(nm(o)) === fold(t));
      if (pick) { e.preventDefault(); window.location.href = hrefFor(pick.name); }
    });
  });

  /* ---------- Popular grid ---------- */
  document.querySelectorAll('[data-popular-grid]').forEach(root => {
    const grid = root.querySelector('[data-grid]');
    const names = splitNames(root.dataset.names);
    const count = +root.dataset.count || 8;
    loadData().then(({ countries }) => {
      const list = names.map(n => findCountry(countries, n)).filter(Boolean).slice(0, count);
      // One markup, two layouts (CSS): a card on desktop, a compact row on phones so more destinations fit on screen.
      // Only Local / Number tags (all popular destinations have 5G, so that tag said nothing). On phones the tag sits right of
      // the name; if a long (translated) name leaves no room it drops below the name instead of being cut.
      grid.innerHTML = list.map(o => `<a class="dest-card dest-card--pop" href="${esc(hrefFor(o.name))}">
        ${flag(o.iso) ? `<img class="flag dest-card__flag" src="${flag(o.iso)}" alt="" width="48" height="48" loading="lazy">` : '<span class="dest-card__flag"></span>'}
        <div class="dest-card__body">
          <div class="dest-card__head"><span class="dest-card__name">${esc(nm(o))}</span>${o.tag ? `<span class="dest-card__tags"><span class="tag tag--red">${esc(PT(o.tag === 'number' ? 'tag_number' : 'tag_local'))}</span></span>` : ''}</div>
          <div class="dest-card__from">${PT('from', { price: `<b>${money(o.from)}</b>` })}</div>
        </div>
        <span class="arrow-dot" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:block" aria-hidden="true" focusable="false"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
      </a>`).join('');
    }).catch(() => { grid.innerHTML = ''; });
  });

  /* ---------- Browse all ---------- */
  document.querySelectorAll('[data-browse]').forEach(root => {
    const tabsEl = root.querySelector('[data-browse-tabs]');
    const listEl = root.querySelector('[data-browse-list]');
    const emptyEl = root.querySelector('[data-browse-empty]');
    const filter = root.querySelector('[data-browse-filter]');
    let D = [], B = [], tab = 'Asia';

    // Versions of one coverage share a name (e.g. two “Hong Kong & Macau”): tell them apart by IP country.
    const dupName = n => B.filter(b => b.name === n).length > 1;
    const render = () => {
      const byCont = c => D.filter(o => o.cont === c);
      const tabs = [...CONTS.map(c => ({ key: c, label: contL(c), count: byCont(c).length })), { key: 'Regional', label: PT('multi_country'), count: B.length }];
      tabsEl.innerHTML = tabs.map(t => `<button type="button" role="tab" aria-selected="${t.key === tab}" class="pill${t.key === tab ? ' is-on' : ''}" data-tab="${esc(t.key)}">${esc(t.label)}<span>${t.count}</span></button>`).join('');
      const ft = fold(filter.value.trim());
      let rows = tab === 'Regional'
        ? B.map(b => ({ name: b.name, label: nm(b), ip: dupName(b.name) ? b.ip : '', flags: b.flags, sub: PT.n('n_dest', b.n), from: b.from, href: b.url || '/pages/destinations?type=multi&q=' + encodeURIComponent(b.name) }))
            .sort((a, b) => col.compare(a.label, b.label))
        : byCont(tab).map(o => ({ name: o.name, label: nm(o), aka: o.aka, iso: o.iso, from: o.from, tag: o.tag, href: hrefFor(o.name) }));
      if (ft) rows = rows.filter(x => hay(x).includes(ft));
      listEl.classList.toggle('grid-browse--multi', tab === 'Regional');
      listEl.innerHTML = rows.map(c => `<a class="browse-row" href="${esc(c.href)}">
        ${c.flags ? flagStack(c.flags, 'flag-stack--row') : flag(c.iso) ? `<img class="flag" src="${flag(c.iso)}" alt="" width="28" height="28" loading="lazy">` : ''}
        ${c.sub ? '<span class="browse-row__main">' : ''}<span class="browse-row__name">${esc(c.label)}${c.ip ? `<span class="browse-row__ip">${esc(PT('ip_in_short', { ip: PT.d(c.ip) }))}</span>` : ''}</span>${c.sub ? `<span class="browse-row__sub">${esc(c.sub)}</span></span>` : ''}${c.tag ? `<span class="red-dot" title="${esc(PT('local_sim_available'))}"></span>` : ''}
        <span class="browse-row__price">${money(c.from)}</span></a>`).join('');
      emptyEl.hidden = rows.length > 0 || D.length === 0;
      if (!emptyEl.hidden) emptyEl.textContent = PT('browse_empty', { where: tab === 'Regional' ? PT('multi_country_plans') : contL(tab), q: filter.value.trim() });
    };

    tabsEl.addEventListener('click', e => {
      const b = e.target.closest('[data-tab]');
      if (b) { tab = b.dataset.tab; render(); }
    });
    filter.addEventListener('input', render);
    loadData().then(d => { D = d.countries; B = d.bundles; render(); }).catch(() => {});
  });

  /* ---------- Destinations list page (design: Pakyo Destinations) ---------- */
  document.querySelectorAll('[data-destinations-page]').forEach(root => {
    const $ = sel => root.querySelector(sel);
    const LINES = {};
    (root.dataset.localLines || '').split('\n').forEach(line => {
      const [name, op, net, cost, num] = line.split('|').map(x => (x || '').trim());
      if (name) LINES[name.toLowerCase()] = { op, net, cost: parseFloat(cost), num: num === 'number' };
    });
    const params = new URLSearchParams(location.search);
    // sort: All/Local tabs; msort: the Multi-country tab keeps its own (null = A–Z for All, most destinations for a region)
    const S = { type: ['local', 'multi'].includes(params.get('type')) ? params.get('type') : 'all', region: 'All', q: params.get('q') || '', sort: 'az', msort: null };
    const MULTI_REGIONS = ['Global', 'Europe', 'Asia', 'Americas', 'Middle East & Central Asia', 'Africa', 'Oceania'];
    const regL = r => r === 'All' ? PT('reg_all') : r === 'Global' ? PT('reg_global') : r === 'Middle East & Central Asia' ? PT('reg_me_ca') : S.type === 'multi' ? PT('reg_' + r.toLowerCase()) : contL(r);
    const regionsFor = type => ['All', ...(type === 'multi' ? MULTI_REGIONS : type === 'local' ? CONTS.filter(c => D.some(o => o.tag && o.cont === c)) : CONTS)];
    // A–Z letter groups only where names start with a Latin letter (not zh / ja / ko)
    const LATIN = !/^(zh|ja|ko)/.test(PT.lang);
    let D = [], B = [];
    const qInput = $('[data-dests-q]');
    qInput.value = S.q;

    const setType = type => {
      Object.assign(S, { type, region: regionsFor(type).includes(S.region) ? S.region : 'All' });
      const u = new URL(location.href);
      if (type === 'all') u.searchParams.delete('type'); else u.searchParams.set('type', type);
      history.replaceState(null, '', u);
      render();
    };

    const card = c => {
      const inner = `<div class="dest-tile__top">${c.flags ? flagStack(c.flags, 'flag-stack--card') : c.iso ? `<img class="flag" src="${flag(c.iso)}" alt="" width="42" height="42" loading="lazy">` : '<span></span>'}${c.tag ? `<span class="tag ${c.tagDark ? 'tag--dark' : 'tag--soft'}">${esc(c.tag)}</span>` : ''}</div>
        <div><div class="dest-tile__name">${esc(c.label)}</div><div class="dest-tile__sub">${esc(c.sub)}</div></div>
        <div class="dest-tile__foot"><span class="dest-tile__from">${PT('from', { price: `<b>${money(c.cost)}</b>` })}</span>${c.href ? '<span class="arrow-dot" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:block" aria-hidden="true" focusable="false"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>' : ''}</div>`;
      const cls = c.flags ? 'dest-tile dest-tile--multi' : 'dest-tile';
      return c.href ? `<a class="${cls}" href="${esc(c.href)}">${inner}</a>` : `<div class="${cls} is-static">${inner}</div>`;
    };

    function render() {
      const locals = D.filter(o => o.tag);
      let items;
      if (S.type === 'local') items = locals.map(o => { const L = LINES[o.name.toLowerCase()] || {}; const num = L.num || o.tag === 'number';
        return { name: o.name, label: nm(o), aka: o.aka, cont: o.cont, cost: isNaN(L.cost) ? o.from : L.cost, iso: o.iso, sub: L.op ? `${PT.op(L.op)} · ${L.net}` : PT('local_network'), tag: PT(num ? 'plus_number' : 'local_network'), tagDark: num, href: pageFor(o.name) }; });
      else if (S.type === 'multi') items = B.map(b => ({ name: b.name, label: nm(b), cont: '', regions: b.regions || [], n: b.n, cost: b.from, flags: b.flags, sub: b.ip ? PT('n_countries_ip', { n: b.n, ip: PT.d(b.ip) }) : PT('n_countries_one_esim', { n: b.n }), href: b.url || '' }));
      else items = D.map(o => ({ name: o.name, label: nm(o), aka: o.aka, cont: o.cont, cost: o.from, iso: o.iso, sub: `${contL(o.cont)} · ${o.g5 ? '5G' : '4G'}`, tag: o.tag ? PT(o.tag === 'number' ? 'plus_number' : 'tag_local') : '', tagDark: o.tag === 'number', href: pageFor(o.name) }));
      if (S.region !== 'All') items = items.filter(x => S.type === 'multi' ? x.regions.includes(S.region) : x.cont === S.region);
      const t = fold(S.q.trim());
      if (t) items = items.filter(x => hay(x).includes(t));
      const sort = S.type === 'multi' ? S.msort || (S.region === 'All' ? 'az' : 'most') : S.sort;
      const az = PT.lang === 'en' ? (a, b) => a.name.localeCompare(b.name) : (a, b) => col.compare(a.label, b.label);
      items.sort(sort === 'price' ? (a, b) => a.cost - b.cost : sort === 'most' ? (a, b) => b.n - a.n || az(a, b) : az);

      const meta = {
        all: [PT('all_dests'), PT('all_dests_sub', { n: D.length || 173 }), PT('search_country')],
        local: [PT('local_title'), PT('local_sub'), PT('search_local')],
        multi: [PT('multi_title'), PT('multi_sub'), PT('search_multi')]
      }[S.type];
      $('[data-dests-crumb]').textContent = meta[0];
      $('[data-dests-title]').textContent = meta[0];
      $('[data-dests-sub]').textContent = meta[1];
      qInput.placeholder = meta[2];
      document.title = `${meta[0]} · ${document.title.split(' · ').pop()}`;

      $('[data-dests-types]').innerHTML = [['all', PT('reg_all'), D.length], ['local', PT('local_title'), locals.length], ['multi', PT('multi_country'), B.length]]
        .map(([k, l, c]) => `<button type="button" role="tab" class="seg__btn${S.type === k ? ' is-on' : ''}" aria-selected="${S.type === k}" data-type="${k}">${esc(l)}<span>${c}</span></button>`).join('');
      const regions = $('[data-dests-regions]');
      const regs = regionsFor(S.type);
      regions.hidden = regs.length < 3;
      regions.innerHTML = regs.map(r => `<button type="button" class="pill pill--sm${S.region === r ? ' is-on' : ''}" data-region="${esc(r)}">${esc(regL(r))}</button>`).join('');
      const sel = $('[data-dests-sort]');
      sel.querySelector('option[value="most"]').hidden = S.type !== 'multi';
      sel.value = sort;

      $('[data-dests-count]').textContent = PT.n(S.type === 'multi' ? 'n_plans' : 'n_dest', items.length);
      // Everything is shown at once (flags lazy-load). All tab, A–Z, no filter: one grid per letter so rows never break
      // mid-page. Multi-country tab with a region: whole-region plans first, then small combinations.
      const grid = xs => `<div class="dests-grid">${xs.map(card).join('')}</div>`;
      let groups = null;
      if (S.type === 'all' && S.region === 'All' && !t && sort === 'az' && LATIN) {
        groups = [];
        items.forEach(x => { const l = fold(x.label).charAt(0).toUpperCase(); const g = groups[groups.length - 1];
          if (g && g.key === l) g.items.push(x); else groups.push({ key: l, head: `<h2 class="dests-letter">${esc(l)}</h2>`, items: [x] }); });
      } else if (S.type === 'multi' && !['All', 'Global'].includes(S.region)) {
        const big = items.filter(x => x.n >= 6), small = items.filter(x => x.n < 6);
        if (big.length && small.length) groups = [{ head: `<div class="dests-section">${esc(PT('sec_whole_region'))}</div>`, items: big },
                                                  { head: `<div class="dests-section">${esc(PT('sec_small'))}</div>`, items: small }];
      }
      $('[data-dests-grid]').innerHTML = groups ? groups.map(g => `<div class="dests-group">${g.head}${grid(g.items)}</div>`).join('') : grid(items);
      $('[data-dests-more]').hidden = true;
      const empty = $('[data-dests-empty]');
      empty.hidden = !(items.length === 0 && D.length > 0);
      empty.querySelector('.dests-empty__title').textContent = PT('nothing_matches', { q: S.q.trim() });
    }

    // The filter bar is sticky (tall on phones). When a search or filter shortens the list while the page is scrolled,
    // the first results can end up under the bar: scroll back so the result count sits just below it.
    const bar = root.querySelector('.dests-bar');
    const reveal = () => requestAnimationFrame(() => {
      const c = $('[data-dests-count]');
      if (!bar || !c) return;
      const gap = c.getBoundingClientRect().top - bar.getBoundingClientRect().bottom - 12;
      if (gap < 0) window.scrollBy(0, gap);
    });
    root.addEventListener('click', e => {
      const b = e.target.closest('button');
      if (!b) return;
      if (b.dataset.type) setType(b.dataset.type);
      else if (b.dataset.region) { S.region = b.dataset.region; render(); }
      else if (b.hasAttribute('data-dests-reset')) { S.q = ''; qInput.value = ''; setType('all'); }
      else return;
      reveal();
    });
    qInput.addEventListener('input', () => { S.q = qInput.value; render(); reveal(); });
    $('[data-dests-sort]').addEventListener('change', e => { if (S.type === 'multi') S.msort = e.target.value; else S.sort = e.target.value; render(); reveal(); });
    loadData().then(d => { D = d.countries; B = d.bundles; render(); }).catch(() => {});
    render();
  });
})();
